import { Box, Typography } from "@mui/joy";
import type { ReactNode } from "react";

import type { MaterialFormulaFlag } from "../../types";
import {
    FormulaExpression,
    formulaHandwritingSx,
    formulaSectionHeadingSx,
} from "../../utils/formatFormulaExpression";
import { formulaTooltipMaxWidth } from "./materialRowLayout";

function formatFormulaDisplayValue(value: number | string): string {
    const num = Number(value);
    if (!Number.isNaN(num) && Number.isFinite(num)) {
        return Number.isInteger(num)
            ? String(num)
            : String(Math.round(num * 100) / 100);
    }
    return String(value);
}

const formulaTooltipSx = {
    maxWidth: formulaTooltipMaxWidth,
    mx: -1.25,
    my: -0.75,
    py: 0,
    px: 0,
    overflow: "hidden",
    borderRadius: 0,
} as const;

const formulaTooltipContentInsetPx = 1.25;

const formulaValuesColumnSx = {
    borderLeft: "1px solid",
    borderColor: "neutral.200",
    width: "max-content",
    minWidth: 100,
    maxWidth: 132,
    flexShrink: 0,
} as const;

const formulaSectionTitleBarSx = {
    ...formulaSectionHeadingSx,
    display: "block",
    px: formulaTooltipContentInsetPx,
    py: 0.625,
    bgcolor: "background.surface",
    borderBottom: "1px solid",
    borderColor: "neutral.200",
} as const;

const formulaSectionBodySx = {
    px: formulaTooltipContentInsetPx,
    pt: 0.75,
    pb: 0.25,
} as const;

const formulaContentSx = formulaHandwritingSx;

function FormulaTooltipSection({
    title,
    children,
    mb = 0,
}: {
    title: string;
    children: ReactNode;
    mb?: number;
}) {
    return (
        <Box
            sx={{
                mb,
                borderBottom: "1px solid",
                borderColor: "neutral.200",
                "&:last-of-type": {
                    borderBottom: "none",
                },
            }}
        >
            <Typography
                component="div"
                level="body-xs"
                className="formula-section-title-bar"
                sx={formulaSectionTitleBarSx}
            >
                {title}
            </Typography>
            <Box sx={formulaSectionBodySx}>{children}</Box>
        </Box>
    );
}

function FormulaValuesList({
    variables,
}: {
    variables: MaterialFormulaFlag["variables"];
}) {
    if (!variables?.length) {
        return (
            <Typography
                level="body-xs"
                sx={{
                    color: "neutral.500",
                    fontFamily: 'var(--std-font-family, "Outfit", sans-serif)',
                    fontSize: "0.75rem",
                }}
            >
                —
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                ...formulaContentSx,
            }}
        >
            {variables.map((variable) => (
                <Box
                    key={variable.name}
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.5,
                    }}
                >
                    <Typography
                        level="body-xs"
                        sx={{
                            fontWeight: 600,
                            color: "primary.700",
                            minWidth: 0,
                            fontFamily: "inherit",
                            fontSize: "inherit",
                        }}
                    >
                        {variable.name}
                    </Typography>
                    <Typography
                        level="body-xs"
                        sx={{
                            color: "neutral.500",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                        }}
                    >
                        =
                    </Typography>
                    <Typography
                        level="body-xs"
                        sx={{
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: 600,
                            fontFamily: "inherit",
                            fontSize: "inherit",
                        }}
                    >
                        {formatFormulaDisplayValue(variable.value)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function FormulaTooltipResult({
    result,
}: {
    result: number | null | undefined;
}) {
    if (result == null) return null;

    return (
        <Typography
            level="body-xs"
            sx={{
                ...formulaContentSx,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                px: formulaTooltipContentInsetPx,
                py: 0.75,
                borderTop: "1px solid",
                borderColor: "neutral.200",
            }}
        >
            = {formatFormulaDisplayValue(result)}
        </Typography>
    );
}

function FormulaTooltipActiveLayout({ formula }: { formula: MaterialFormulaFlag }) {
    const hasValues = !!formula.variables?.length;
    const hasEvaluated = !!formula.substitutedExpression;

    if (!hasValues) {
        return (
            <>
                {formula.expression && (
                    <FormulaTooltipSection title="Formula" mb={0}>
                        <FormulaExpression expression={formula.expression} />
                    </FormulaTooltipSection>
                )}
                {hasEvaluated && (
                    <FormulaTooltipSection title="Evaluated" mb={0}>
                        <FormulaExpression
                            expression={formula.substitutedExpression!}
                        />
                    </FormulaTooltipSection>
                )}
                <FormulaTooltipResult result={formula.result} />
            </>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "stretch" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        component="div"
                        level="body-xs"
                        sx={formulaSectionTitleBarSx}
                    >
                        Formula
                    </Typography>
                    <Box
                        sx={{
                            ...formulaSectionBodySx,
                            pb: hasEvaluated ? 0.75 : 0.25,
                            borderBottom: hasEvaluated ? "1px solid" : "none",
                            borderColor: "neutral.200",
                        }}
                    >
                        {formula.expression ? (
                            <FormulaExpression expression={formula.expression} />
                        ) : (
                            <Typography
                                level="body-xs"
                                sx={{ color: "neutral.500", ...formulaContentSx }}
                            >
                                —
                            </Typography>
                        )}
                    </Box>

                    {hasEvaluated && (
                        <>
                            <Typography
                                component="div"
                                level="body-xs"
                                sx={formulaSectionTitleBarSx}
                            >
                                Evaluated
                            </Typography>
                            <Box sx={formulaSectionBodySx}>
                                <FormulaExpression
                                    expression={formula.substitutedExpression!}
                                />
                            </Box>
                        </>
                    )}
                </Box>

                <Box sx={formulaValuesColumnSx}>
                    <Typography
                        component="div"
                        level="body-xs"
                        sx={formulaSectionTitleBarSx}
                    >
                        Values
                    </Typography>
                    <Box sx={{ ...formulaSectionBodySx, pb: 0.75 }}>
                        <FormulaValuesList variables={formula.variables} />
                    </Box>
                </Box>
            </Box>
            <FormulaTooltipResult result={formula.result} />
        </>
    );
}

export function FormulaTooltipContent({
    formula,
}: {
    formula?: MaterialFormulaFlag;
}) {
    if (!formula?.hasFormula) return null;

    if (!formula.formulaActive) {
        return (
            <Box sx={formulaTooltipSx}>
                {formula.expression && (
                    <FormulaTooltipSection title="Formula" mb={0}>
                        <FormulaExpression expression={formula.expression} />
                    </FormulaTooltipSection>
                )}
                <Typography
                    level="body-xs"
                    sx={{
                        color: "neutral.600",
                        fontFamily:
                            'var(--std-font-family, "Outfit", sans-serif)',
                        px: formulaTooltipContentInsetPx,
                        py: 1,
                    }}
                >
                    Select a product to calculate quantity
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={formulaTooltipSx}>
            <FormulaTooltipActiveLayout formula={formula} />
        </Box>
    );
}
